import i18next from "i18next";
import { matchPath } from "react-router-dom";
import { z } from "zod";

import { DEFAULT_LANGUAGE, normalizeLanguage } from "./localization";

/**
 * The NGFT Crew tutorial is a static site published on help.ngft.com, behind the Keycloak login.
 * help-map.json, published with it, maps CO paths to chapter pages, so the "?" button follows the
 * tutorial without code changes here. help.ngft.com serves that one file without login (CORS allowed).
 * It is published apart from CO, so it is checked before use: a broken file would break the header.
 */
const page = z.string().regex(/^[a-z0-9-]+$/);
const helpMapSchema = z.object({
  defaultLanguage: z.string().regex(/^[a-z]{2}$/),
  languages: z.array(z.string()),
  fallbackPage: page,
  routes: z.array(z.object({ path: z.string(), page })),
});
export type HelpMap = z.infer<typeof helpMapSchema>;

/** Where the tutorial is published (e.g. https://help.ngft.com/crew); without it CO shows no help links. */
export const helpBaseUrl = (): string | undefined => process.env.REACT_APP_HELP_BASE_URL;

let helpMap: HelpMap | null = null;

// Called once at startup. If it fails or the file is not a valid map, helpMap stays null and every link opens the
// table of contents.
export const loadHelpMap = (): Promise<void> =>
  helpBaseUrl()
    ? fetch(`${helpBaseUrl()}/help-map.json`)
        .then((response) => (response.ok ? response.json() : null))
        .then((map) => {
          helpMap = helpMapSchema.safeParse(map).data ?? null;
        })
        .catch(() => {})
    : Promise.resolve();

// First matching route wins, so the file lists the more specific paths first.
export const helpPageFor = (pathname: string, map: HelpMap | null = helpMap): string =>
  map?.routes.find((route) => matchPath(pathname, { path: route.path, exact: false }))?.page ??
  map?.fallbackPage ??
  "index";

// The tutorial follows the language CO shows; languages the tutorial does not have yet open in its default.
export const helpUrl = (page: string, map: HelpMap | null = helpMap): string => {
  const language = normalizeLanguage(i18next.language);
  const helpLanguage =
    language && map?.languages.includes(language) ? language : (map?.defaultLanguage ?? DEFAULT_LANGUAGE);
  return `${helpBaseUrl()}/${helpLanguage}/${page}.html`;
};
